import { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faSpinner, faCheck, faLocationDot, faPhone, faHashtag } from "@fortawesome/free-solid-svg-icons";
import crudApi from "@serie3/common/api/crudApi";
import CrmConfig from "@serie3/freelancesplaceapi/Config/Crm/CrmConfig";

const Section = ({ icon, title, items, renderItem }) => {
  if (!items?.length) return null;
  return (
    <div>
      <div style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#9e9e9e", marginBottom: "8px", display: "flex", alignItems: "center", gap: "6px" }}>
        <FontAwesomeIcon icon={icon} />
        {title}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        {items.map((item, i) => (
          <div key={i} style={{ background: "#f8f9fb", borderRadius: "8px", padding: "10px 14px", fontSize: "0.85rem", color: "#424242" }}>
            {renderItem(item)}
          </div>
        ))}
      </div>
    </div>
  );
};

const AiResearchButton = ({
  personId,
  companyId,
  formik,
  setContactData,
  setAddressData,
  setMediaData,
  autoTrigger = false,
  onClose,
  style: btnStyle,
}) => {
  const [loading,  setLoading]  = useState(false);
  const [result,   setResult]   = useState(null);
  const [applying, setApplying] = useState(false);
  const [applied,  setApplied]  = useState(false);

  useEffect(() => {
    if (autoTrigger) handleResearch();
  }, []);

  const isPerson = !!personId;
  const entityId = personId ?? companyId;
  const endpoint = isPerson
    ? `/crm/ai/research-person/${entityId}`
    : `/crm/ai/research-company/${entityId}`;

  const handleResearch = async () => {
    setLoading(true);
    setResult(null);
    setApplied(false);
    try {
      const res = await crudApi.justGet(endpoint);
      setResult(res?.data ?? null);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!result) return;
    setApplying(true);

    // Apply basic fields to formik (only overwrite empty values)
    if (formik) {
      const updates = {};
      if (isPerson && result.person) {
        const p = result.person;
        if (!formik.values.vorname   && p.vorname)   updates.vorname   = p.vorname;
        if (!formik.values.name      && p.name)      updates.name      = p.name;
        if (!formik.values.anrede    && p.anrede)    updates.anrede    = p.anrede;
        if (!formik.values.geschlecht && p.geschlecht) updates.geschlecht = p.geschlecht;
        if (!formik.values.position  && p.position)  updates.position  = p.position;
        if (!formik.values.geburtstag && p.geburtstag) updates.geburtstag = p.geburtstag;
      } else if (!isPerson && result.company) {
        const c = result.company;
        if (!formik.values.firmenname  && c.firmenname)  updates.firmenname  = c.firmenname;
        if (!formik.values.rechtsform  && c.rechtsform)  updates.rechtsform  = c.rechtsform;
        if (!formik.values.tax_id      && c.taxId)       updates.tax_id      = c.taxId;
        if (!formik.values.tax_number  && c.taxNumber)   updates.tax_number  = c.taxNumber;
      }
      if (Object.keys(updates).length) {
        formik.setValues({ ...formik.values, ...updates });
        formik.submitForm();
      }
    }

    const owner = isPerson ? { person: { id: entityId } } : { kunde: { id: entityId } };

    // Save new addresses
    if (result.adressen?.length && setAddressData) {
      const saved = [];
      for (const a of result.adressen) {
        const res = await crudApi.createNew(
          CrmConfig.address.endpoints.new,
          JSON.stringify({ strasse: a.strasse, hausnummer: a.hausnummer, plz: a.plz, ort: a.ort, ...owner })
        );
        if (res?.data) saved.push(res.data);
      }
      if (saved.length) setAddressData(prev => [...(prev ?? []), ...saved]);
    }

    // Save new contact data
    if (result.kontaktdaten?.length && setContactData) {
      const saved = [];
      for (const k of result.kontaktdaten) {
        const res = await crudApi.createNew(
          CrmConfig.contactData.endpoints.new,
          JSON.stringify({ key_name: k.key_name, value: k.value, ...owner })
        );
        if (res?.data) saved.push(res.data);
      }
      if (saved.length) setContactData(prev => [...(prev ?? []), ...saved]);
    }

    // Save new social media
    if (result.sozialeMedien?.length && setMediaData) {
      const saved = [];
      for (const s of result.sozialeMedien) {
        const res = await crudApi.createNew(
          CrmConfig.socialMedia.endpoints.new,
          JSON.stringify({ key_name: s.key_name, value: s.value, date: s.date ?? new Date().toISOString().split("T")[0], ...owner })
        );
        if (res?.data) saved.push(res.data);
      }
      if (saved.length) setMediaData(prev => [...(prev ?? []), ...saved]);
    }

    setApplying(false);
    setApplied(true);
  };

  const canApply = !!(formik || setContactData || setAddressData || setMediaData);
  const hasResult = !!(result?.person || result?.company || result?.adressen?.length || result?.kontaktdaten?.length || result?.sozialeMedien?.length);

  return (
    <>
      <button
        type="button"
        onClick={handleResearch}
        disabled={loading}
        style={{
          padding: "9px 14px", border: "none", borderRadius: "10px",
          fontWeight: 700, fontSize: "0.85rem", cursor: loading ? "default" : "pointer",
          background: "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)",
          color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
          gap: "7px", width: "100%", transition: "opacity 0.15s",
          opacity: loading ? 0.75 : 1,
          ...btnStyle,
        }}
      >
        <FontAwesomeIcon icon={loading ? faSpinner : faMagnifyingGlass} spin={loading} style={{ fontSize: "0.8rem" }} />
        {loading ? "Researching…" : "AI Research"}
      </button>

      {(loading || result) && (
        <Modal show onHide={() => { setResult(null); setLoading(false); onClose?.(); }} centered contentClassName="modal-ai-research">
          <style>{`.modal-ai-research { background: transparent !important; border: none !important; box-shadow: none !important; }`}</style>

          <div style={{ background: "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)", padding: "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", borderRadius: "14px 14px 0 0" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <FontAwesomeIcon icon={faMagnifyingGlass} style={{ color: "#fff", fontSize: "0.9rem" }} />
              <span style={{ fontWeight: 700, fontSize: "1rem", color: "#fff" }}>AI Research Results</span>
            </div>
            <button onClick={() => setResult(null)} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "8px", width: "30px", height: "30px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", fontSize: "1rem" }}>✕</button>
          </div>

          <div style={{ padding: "24px", background: "#f8f9fb", display: "flex", flexDirection: "column", gap: "16px", maxHeight: "60vh", overflowY: "auto" }}>
            {loading && (
              <div style={{ textAlign: "center", padding: "32px", color: "#7c3aed" }}>
                <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: "1.8rem", marginBottom: "12px", display: "block", margin: "0 auto 12px" }} />
                <div style={{ fontWeight: 600, fontSize: "0.88rem", color: "#616161" }}>Researching, this may take a moment…</div>
              </div>
            )}
            {!loading && !hasResult && (
              <p style={{ color: "#9e9e9e", textAlign: "center", padding: "20px 0", margin: 0 }}>No additional data found.</p>
            )}

            {/* Basic fields */}
            {!loading && (result?.person || result?.company) && (() => {
              const fields = result.person
                ? Object.entries(result.person).filter(([, v]) => v)
                : Object.entries(result.company).filter(([, v]) => v);
              if (!fields.length) return null;
              return (
                <div>
                  <div style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#9e9e9e", marginBottom: "8px" }}>
                    Basic Info
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                    {fields.map(([key, val]) => (
                      <div key={key} style={{ background: "#fff", borderRadius: "8px", padding: "10px 14px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
                        <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "#9e9e9e", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "2px" }}>{key}</div>
                        <div style={{ fontSize: "0.88rem", fontWeight: 600, color: "#212121" }}>{String(val)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {!loading && <>
              <Section icon={faLocationDot} title="Addresses" items={result?.adressen}
                renderItem={a => `${[a.strasse, a.hausnummer].filter(Boolean).join(" ")}, ${[a.plz, a.ort].filter(Boolean).join(" ")}`}
              />
              <Section icon={faPhone} title="Contact Data" items={result?.kontaktdaten}
                renderItem={k => <><span style={{ color: "#9e9e9e", marginRight: "6px" }}>{k.key_name}:</span>{k.value}</>}
              />
              <Section icon={faHashtag} title="Social Media" items={result?.sozialeMedien}
                renderItem={s => <><span style={{ color: "#9e9e9e", marginRight: "6px" }}>{s.key_name}:</span>{s.value}</>}
              />
            </>}
          </div>

          <div style={{ background: "#fff", borderTop: "1px solid #f0f0f0", padding: "14px 24px", display: "flex", justifyContent: "flex-end", gap: "10px", borderRadius: "0 0 14px 14px" }}>
            <button onClick={() => { setResult(null); setLoading(false); onClose?.(); }} style={{ padding: "8px 18px", background: "#f5f5f5", color: "#424242", border: "none", borderRadius: "8px", fontWeight: 600, fontSize: "0.88rem", cursor: "pointer" }}>
              Close
            </button>
            {canApply && hasResult && (
              <button
                onClick={handleApply}
                disabled={applying || applied}
                style={{ padding: "8px 20px", background: applied ? "#2e7d32" : "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)", color: "#fff", border: "none", borderRadius: "8px", fontWeight: 700, fontSize: "0.88rem", cursor: applying || applied ? "default" : "pointer", display: "flex", alignItems: "center", gap: "7px", opacity: applying ? 0.75 : 1 }}
              >
                <FontAwesomeIcon icon={applied ? faCheck : applying ? faSpinner : faMagnifyingGlass} spin={applying} style={{ fontSize: "0.78rem" }} />
                {applied ? "Applied!" : applying ? "Applying…" : "Apply Missing Data"}
              </button>
            )}
          </div>
        </Modal>
      )}
    </>
  );
};

export default AiResearchButton;
